/**
 * FilterBuilder Component
 *
 * Container for managing multiple filter conditions.
 * Allows adding, editing, and removing filters (up to maxFilters).
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { FilterRow, type FilterCondition } from './FilterRow';
import type { ContextCategory } from '../types/variables';
import { Icon } from '@/shared/icons'
export interface FilterBuilderProps {
  /** Array of filter conditions */
  filters: FilterCondition[];
  /** Handler when filters change */
  onChange: (filters: FilterCondition[]) => void;
  /** Available entities for variable autocomplete */
  availableEntities: ContextCategory[];
  /** Maximum number of filters allowed */
  maxFilters?: number;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * Create an empty filter condition
 */
function createEmptyFilter(): FilterCondition {
  return {
    field: '',
    operator: 'equals',
    value: '',
  };
}

/**
 * FilterBuilder - Container for managing multiple filter conditions
 */
export const FilterBuilder: React.FC<FilterBuilderProps> = ({
  filters,
  onChange,
  availableEntities,
  maxFilters = 5,
  disabled = false,
}) => {
  // Stable keys for filter rows (append-only ids; removal splices) so that
  // removing a middle row doesn't remount the wrong inputs (#30).
  const keysRef = React.useRef<string[]>([]);
  while (keysRef.current.length < filters.length) {
    keysRef.current.push(`f-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  }

  // Handle adding a new filter
  const handleAddFilter = () => {
    if (filters.length >= maxFilters) return;
    onChange([...filters, createEmptyFilter()]);
  };

  // Handle updating a filter
  const handleUpdateFilter = (index: number, filter: FilterCondition) => {
    const newFilters = [...filters];
    newFilters[index] = filter;
    onChange(newFilters);
  };

  // Handle removing a filter
  const handleRemoveFilter = (index: number) => {
    keysRef.current.splice(index, 1);
    const newFilters = filters.filter((_, i) => i !== index);
    onChange(newFilters);
  };

  const canAddMore = filters.length < maxFilters;
  const hasFilters = filters.length > 0;

  return (
    <div className="space-y-4">

      {/* Filter rows */}
      {hasFilters ? (
        <div className="space-y-3">
          {filters.map((filter, index) => (
            <FilterRow
              key={keysRef.current[index] ?? index}
              filter={filter}
              onChange={(f) => handleUpdateFilter(index, f)}
              onRemove={() => handleRemoveFilter(index)}
              availableEntities={availableEntities}
              index={index}
              disabled={disabled}
            />
          ))}
        </div>
      ) : (
        <div
          className="p-6 text-center rounded-lg border-2 border-dashed"
          style={{ borderColor: 'var(--theme-card-border)' }}
        >
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            Nenhum filtro adicionado.
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)', opacity: 0.7 }}>
            Clique no botão abaixo para adicionar um filtro.
          </p>
        </div>
      )}

      {/* Add button */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handleAddFilter}
          disabled={disabled || !canAddMore}
          data-testid="filter-builder-add"
          className={cn(
            'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
            canAddMore
              ? 'text-violet-700 bg-violet-50 hover:bg-violet-100'
              : 'cursor-not-allowed'
          )}
          style={!canAddMore ? { color: 'var(--theme-muted-text)', backgroundColor: 'var(--theme-button-ghost-hover)' } : undefined}
        >
          <Icon name="plus" className="w-4 h-4" />
          Adicionar Filtro
        </button>

        {/* Counter */}
        <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          {filters.length} / {maxFilters} filtros
        </span>
      </div>

      {/* Max reached warning */}
      {!canAddMore && (
        <p className="text-xs text-amber-600">
          Limite máximo de {maxFilters} filtros atingido.
        </p>
      )}
    </div>
  );
};

FilterBuilder.displayName = 'FilterBuilder';

export default FilterBuilder;
